import {
  BarChart3,
  Boxes,
  Briefcase,
  Settings,
  Building2,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  CircleAlert,
  ClipboardList,
  Clock,
  DollarSign,
  DatabaseZap,
  FileUp,
  History,
  LayoutDashboard,
  Receipt,
  Shield,
  ShieldCheck,
  Table2,
  Tags,
  UserCog,
  Users,
  UsersRound,
  Workflow,
} from 'lucide-react'
import type { NavGroup, NavItem } from '../ui/patterns/AppShell'

/**
 * What the sidebar offers, as data.
 *
 * Its own module rather than living inside the shell component, for two
 * reasons. The invariant test reads the real configuration here rather than a
 * second copy of the ids maintained by hand, which would agree with itself
 * while the product was wrong. And a file that exports both a component and
 * configuration breaks fast refresh, which the linter is right to object to.
 *
 * Nothing here renders. It decides what exists and who may see it, which is
 * deliberately separate from how the menu groups it.
 */

export interface GuardedNavItem extends NavItem {
  readonly path: string
  /** Whether this user may see the item at all. */
  readonly visible: boolean
}

/**
 * The one flat list, built for a given audience.
 *
 * Module scope rather than inside the component so the invariant test can
 * read the real configuration. Nothing here renders; it decides what exists
 * and who may see it, which is deliberately separate from how it is grouped.
 */
export function buildDestinations(
  session: NavAudience,
  isAdministrator: boolean,
): readonly GuardedNavItem[] {
  return [
      { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', path: '/', available: true, visible: true },

      /* ------------------------------------------------------------ reports -- */
      {
        id: 'costing',
        icon: DollarSign,
        label: 'Labour cost',
        path: '/costing',
        available: true,
        // Wage-derived figures, so the link needs financial access as well as
        // administration. RLS still decides what the page receives.
        visible: session.canViewFinancials && isAdministrator,
      },
      {
        id: 'jobs-report',
        icon: Briefcase,
        /*
         * "Job costs", not "Jobs", since M10C gave the operational screens the
         * plain word.
         *
         * Two entries labelled Jobs is duplicate navigation: it reads as one
         * destination listed twice, and to a screen reader both are simply
         * "Jobs button". This one answers what a job cost, which is what the
         * page has always shown. Same resolution the roster and the labour
         * report reached, and the id changed too, because it collided with the
         * operational entry and the later one silently won the lookup.
         */
        label: 'Job costs',
        path: '/reports/jobs',
        available: true,
        visible: true,
      },
      {
        id: 'employees-report',
        icon: Users,
        label: 'Employees',
        path: '/reports/employees',
        available: true,
        visible: true,
      },
      {
        id: 'overtime-report',
        icon: Clock,
        label: 'Overtime',
        path: '/reports/overtime',
        available: true,
        // Cannot be calculated without the restricted pay rate at all, so
        // offering the link to somebody who can only be refused would be a
        // promise the product cannot keep.
        visible: session.canViewFinancials,
      },
      {
        id: 'schedule-outcomes',
        icon: CalendarClock,
        label: 'Schedule outcomes',
        path: '/reports/schedule-outcomes',
        available: true,
        /*
         * Visible to everybody who can reach reporting. It names jobs, blocks
         * and days, and no rate, cost or margin, so there is nothing here that
         * financial permission exists to protect.
         *
         * The label says what the data is. "Scheduler performance" would say
         * something about people that these numbers cannot support.
         */
        visible: true,
      },
      {
        id: 'unallocated',
        icon: CircleAlert,
        label: 'Unallocated',
        path: '/reports/unallocated',
        available: true,
        visible: true,
      },
      {
        id: 'exceptions',
        icon: ClipboardList,
        label: 'Exceptions',
        path: '/reports/exceptions',
        available: true,
        // Financial-only for the same reason overtime is: an exception names
        // missing pay configuration for a named employee.
        visible: session.canViewFinancials,
      },

      /* ---------------------------------------------------------- scheduler -- */
    {
      id: 'scheduler',
      icon: CalendarDays,
      label: 'Scheduler',
      path: '/scheduler',
      // Beside Jobs, because planning the work and running the work are the
      // same person's morning. Operational, so a viewer sees it.
      available: true,
      visible: true,
    },

    /* ------------------------------------------------------------- roster -- */
    {
      id: 'roster',
      icon: CalendarRange,
      label: 'Roster',
      path: '/roster',
      /*
       * Operational and top level, beside the Scheduler, never inside Settings.
       *
       * Settings, Workforce holds the rules: ordinary working hours, skills,
       * unavailability. This is the actual planned workforce week, which is a
       * thing a supervisor opens every morning rather than something they
       * configure once.
       *
       * Visible even when nothing is rostered. Rostering is optional and the
       * screen says so in its own empty state, which is more use than hiding a
       * feature until somebody already knows it exists.
       */
      available: true,
      visible: true,
    },

    /* --------------------------------------------------------------- jobs -- */
      {
        id: 'jobs',
        icon: Briefcase,
        label: 'Jobs',
        path: '/jobs',
        // Top level, and deliberately named the same as Reports > Jobs. The
        // group prefix is what distinguishes them, exactly as it does for
        // Employees: this is the work, that is what the work cost.
        available: true,
        visible: true,
      },

      /* ----------------------------------------------------------- products -- */
      {
        id: 'products',
        icon: Boxes,
        label: 'Products',
        path: '/products',
        available: true,
        /*
         * Visible to every member. A worker opens this to reach the drawing
         * their job was ordered against, so hiding it from anybody who is not
         * an administrator would defeat the point of the milestone. Writing is
         * decided by the policies, not by the menu.
         */
        visible: true,
      },

      /* ---------------------------------------------------------- customers -- */
      {
        id: 'customers',
        icon: Building2,
        label: 'Customers',
        path: '/customers',
        // Top level, not inside Settings. A customer is somebody you work for
        // rather than something you configure once, and contacts and sites are
        // reached through the customer rather than as menu items of their own:
        // a site has no meaning without the customer it belongs to.
        available: true,
        // Operational, not financial. A viewer needs to know who the work is for.
        visible: true,
      },

      /* --------------------------------------------------------- timesheets -- */
      {
        id: 'timesheets',
        icon: CalendarClock,
        /*
         * Named for what the reader will find there.
         *
         * A linked worker who is not an administrator sees one person's week,
         * their own, so calling it "Timesheets" would promise the workshop's
         * hours and deliver theirs. An administrator sees everybody, so it
         * keeps the plain word. The permission is unchanged either way: this
         * is a label, and the policies decide what arrives (ADR-109).
         */
        label: !isAdministrator && session.linkedEmployee ? 'My timesheets' : 'Timesheets',
        path: '/timesheets',
        available: true,
        /*
         * Beside Import CSV, because the two are the same act by different
         * means: a typed entry and an imported row are both a
         * timesheet_entries row and both reach every report the moment they
         * exist. Putting them apart would suggest the product holds two kinds
         * of hours, which it does not.
         *
         * Visible to a viewer. Hours worked and who worked them are
         * operational rather than financial, exactly as on /jobs, and the
         * screen offers a viewer nothing to press.
         */
        visible: true,
      },

      /* ------------------------------------------------------------- import -- */
      {
        id: 'import',
        icon: FileUp,
        label: 'Import CSV',
        path: '/imports',
        available: true,
        // Owner or admin only, matching the storage policies on the private
        // bucket. Hiding it from a viewer is courtesy; the route guard and the
        // policies are what refuse.
        visible: isAdministrator,
      },

      /* ---------------------------------------------------------- migration -- */
      {
        id: 'migration',
        icon: DatabaseZap,
        /*
         * Not "Import" again. The two are different jobs: an import is the
         * weekly timesheet file, and a migration is bringing a customer list
         * off the system being left behind. One word for both would make the
         * weekly job look like a one-off and the one-off look routine.
         */
        label: 'Data migration',
        path: '/migrations',
        available: true,
        visible: isAdministrator,
      },

      /* --------------------------------------------------- people, in settings -- */
      {
        id: 'employees',
        icon: Users,
        /*
         * "Employees" again, now that a group name carries the distinction.
         *
         * It became "Employee Records" when the labour report took the word,
         * which resolved the collision by making one of them awkward. Settings >
         * Employees is the roster; Reports > Employees is what they cost.
         */
        label: 'Employees',
        path: '/settings/employees',
        available: true,
        // Who works here is not financial data, so a viewer sees the roster. The
        // pay amounts inside are a separate decision, made by RLS.
        visible: true,
      },
      {
        id: 'admin',
        icon: UserCog,
        // People who can sign in, as opposed to people whose labour is recorded.
        label: 'Users',
        path: '/admin/users',
        available: true,
        visible: isAdministrator,
      },

      /* ----------------------------------------------------------- settings -- */
      {
        id: 'settings',
        icon: Settings,
        label: 'Organisation',
        path: '/settings/organisation',
        available: true,
        visible: isAdministrator,
      },
      {
        id: 'wage-matrix',
        icon: Table2,
        label: 'Wage matrix',
        path: '/settings/wage-matrix',
        available: true,
        // Wage rates are financial data and the page is administration, so the
        // link needs both.
        visible: session.canViewFinancials && isAdministrator,
      },
      {
        id: 'overtime',
        icon: Clock,
        label: 'Overtime rules',
        path: '/settings/overtime',
        available: true,
        visible: isAdministrator,
      },
      {
        id: 'billing',
        icon: Receipt,
        label: 'Billing rules',
        path: '/settings/billing',
        available: true,
        visible: isAdministrator,
      },
      {
        id: 'holidays',
        icon: CalendarDays,
        label: 'Public holidays',
        path: '/settings/public-holidays',
        available: true,
        visible: isAdministrator,
      },
      {
        id: 'outcome-reasons',
        icon: ClipboardList,
        label: 'Outcome reasons',
        path: '/settings/outcome-reasons',
        available: true,
        /*
         * Configuration, so administrators. Every member can still *read* the
         * labels through RLS, which is what makes a recorded block readable to
         * the person who worked on it.
         */
        visible: isAdministrator,
      },
      {
        id: 'job-statuses',
        icon: Workflow,
        label: 'Job statuses',
        path: '/settings/job-statuses',
        available: true,
        visible: isAdministrator,
      },
      {
        id: 'workforce',
      icon: UsersRound,
      label: 'Availability and skills',
      path: '/settings/workforce',
      available: true,
      visible: isAdministrator,
    },
    {
      id: 'job-categories',
        icon: Tags,
        label: 'Job categories',
        path: '/settings/job-categories',
        available: true,
        visible: isAdministrator,
      },
      {
        id: 'readiness',
        icon: ShieldCheck,
        label: 'Readiness',
        path: '/settings/readiness',
        available: true,
        visible: isAdministrator,
      },
      {
        id: 'audit-history',
        icon: History,
        label: 'Audit history',
        path: '/settings/audit-history',
        available: true,
        visible: isAdministrator,
      },

      /* ----------------------------------------------------------- platform -- */
      {
        id: 'platform',
        icon: Shield,
        // Deliberately top level and outside Settings. It administers the
        // platform rather than this tenant, and a platform administrator holds no
        // membership of the organisation whose settings would contain it.
        label: 'Platform',
        path: '/platform/organisations',
        available: true,
        visible: session.isPlatformAdmin,
      },
  ]
}

/**
 * The permissions a destination list is built for.
 *
 * Narrower than the whole session on purpose: everything the list depends on is
 * here, so a test can build the real configuration without assembling a
 * session.
 */
export interface NavAudience {
  readonly role: string | null
  readonly canViewFinancials: boolean
  readonly isPlatformAdmin: boolean
  /** Whether this login is linked to an employee, which names one entry. */
  readonly linkedEmployee?: boolean
}

/**
 * Every destination, before permission filtering and before grouping.
 *
 * Exported so `tests/navigation-hierarchy.test.tsx` can assert that no two
 * entries share an id against **this** list rather than against a second copy
 * of the ids maintained by hand, which would agree with itself while the
 * product was wrong.
 *
 * That is not hypothetical. The Reports child and the operational jobs entry
 * both used `id: 'jobs'`; the lookup is a Map, so the later one silently won
 * and Reports > Jobs navigated to /jobs.
 */
export function navDestinations(audience: NavAudience): readonly GuardedNavItem[] {
  const session = audience
  const isAdministrator = audience.role === 'owner' || audience.role === 'admin'
  return buildDestinations(session, isAdministrator)
}

/* ========================================================================== */
/*  Sections: the eight business areas, and what sits inside each             */
/* ========================================================================== */

/**
 * A business area in the sidebar, and the screens inside it.
 *
 * **The sidebar answers "which part of the business am I in"; the tabs answer
 * "which part of that am I looking at".** Before this the sidebar tried to
 * answer both and grew to ten entries, two of which were collapsible groups
 * holding twenty screens between them, so the one list mixed "Jobs" with
 * "Public holidays".
 *
 * Nothing here changes a permission or a path. A section is a view over the
 * same destinations `buildDestinations` already produced, with the same
 * `visible` flags decided in the same place, so a screen a reader could not
 * reach before cannot be reached now by being grouped differently. Every URL is
 * unchanged, which is why no bookmark and no deep link breaks.
 */
export interface NavSection {
  readonly id: string
  readonly label: string
  readonly icon: NonNullable<NavGroup['icon']>
  /** Where pressing the section goes: its first visible screen. */
  readonly path: string
  /**
   * The screens inside it, already filtered by permission. Empty for a section
   * that is a single screen, which is how Jobs, Products and Customers stay one
   * press away rather than becoming a tab of themselves.
   */
  readonly children: readonly GuardedNavItem[]
}

/**
 * Which destinations belong to which area.
 *
 * Two placements are judgement calls worth stating. **Import CSV sits with
 * Timesheets** under Workforce because a typed entry and an imported row are
 * the same `timesheet_entries` row by two routes, which is the reason the two
 * were already adjacent. **Data migration sits in Settings** because bringing a
 * customer list off the old system is something done once at onboarding, not a
 * weekly act.
 */
const SECTION_PLAN: readonly {
  readonly id: string
  readonly label: string
  readonly icon: NonNullable<NavGroup['icon']>
  readonly children: readonly string[]
}[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, children: ['dashboard'] },
  { id: 'jobs', label: 'Jobs', icon: Briefcase, children: ['jobs'] },
  { id: 'schedule', label: 'Schedule', icon: CalendarDays, children: ['scheduler', 'roster'] },
  {
    id: 'workforce',
    label: 'Workforce',
    icon: UsersRound,
    children: ['timesheets', 'import', 'employees', 'workforce'],
  },
  { id: 'products', label: 'Products', icon: Boxes, children: ['products'] },
  { id: 'customers', label: 'Customers', icon: Building2, children: ['customers'] },
  {
    id: 'reports',
    label: 'Reports',
    icon: BarChart3,
    children: [
      'costing',
      'jobs-report',
      'employees-report',
      'overtime-report',
      'schedule-outcomes',
      'unallocated',
      'exceptions',
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    children: [
      'settings',
      'admin',
      'job-statuses',
      'job-categories',
      'outcome-reasons',
      'overtime',
      'billing',
      'holidays',
      'wage-matrix',
      'migration',
      'readiness',
      'audit-history',
    ],
  },
  { id: 'platform', label: 'Platform', icon: Shield, children: ['platform'] },
]

/**
 * The sidebar, as eight areas rather than as a list of screens.
 *
 * A section with no visible child does not appear at all, so a viewer sees
 * fewer areas without anything being hidden that they could previously reach.
 * A section whose only child is itself (Jobs, Products, Customers, Dashboard)
 * carries no tabs: adding a single tab under a heading of the same name is
 * furniture.
 */
export function buildSections(
  destinations: readonly GuardedNavItem[],
): readonly NavSection[] {
  const byId = new Map(destinations.map((item) => [item.id, item]))

  return SECTION_PLAN.flatMap((plan) => {
    const children = plan.children
      .map((id) => byId.get(id))
      .filter((item): item is GuardedNavItem => item !== undefined && item.visible)

    if (children.length === 0) return []

    /*
     * A section named for its only screen shows no tabs. Everything else shows
     * one tab per screen, and the section lands on the first of them.
     */
    const first = children[0]
    if (first === undefined) return []
    const single = children.length === 1 && first.id === plan.id
    return [
      {
        id: plan.id,
        label: plan.label,
        icon: plan.icon,
        path: first.path,
        children: single ? [] : children,
      },
    ]
  })
}
